import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { ReactTable, TabComponent } from '@components';
import { MRT_ToggleGlobalFilterButton } from 'material-react-table';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Menu,
    MenuItem,
    Modal,
    Tooltip,
    Typography
} from '@mui/material';
import AddEntry from './AddEntry';
import FXOrderLog from './FXOrderLog';
import OrderLogDetail from './OrderLogDetail';
import { useDispatch, useSelector } from 'react-redux';
import { apiGetOrder, apiAddFXOrderLog } from '@store';
import { showToast } from '@utils';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment/moment';

// Constants
const FILTER_TYPES = {
    ONE_DAY: 'oneDay',
    ONE_WEEK: 'oneWeek',
    ONE_MONTH: 'oneMonth',
    ALL: 'all'
};

const GVM_COMPANY_DOWNLOAD_FORMAT = [
    'Seller',
    'Buyer',
    'TransactionDate',
    'DealID',
    'UTI',
    'DeliveryPoint',
    'ContractQuantity',
    'StartDeliveryDate',
    'EndDeliveryDate',
    'ContractPrice',
    'Currency',
    'Trader',
    'ContractType',
    'ShareholderProduct',
    'ClientProduct'
];

const DEFAULT_DOWNLOAD_FORMAT = [
    'Contract',
    'Buyer',
    'Seller',
    'DeliveryPoint',
    'Volume',
    'Unit',
    'Price',
    'Currency',
    'Type',
    'Status',
    'Account',
    'CreatedAt',
    'DeliveryStart',
    'DeliveryEnd'
];

// Helper functions
const getDateRange = (days) => {
    const endDate = new Date().toISOString().split('T')[0];
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    return {
        start: startDate.toISOString().split('T')[0],
        end: endDate
    };
};

const filterOrdersByDateRange = (orders, days) => {
    if (!orders?.length) return [];

    const { start, end } = getDateRange(days);
    return orders.filter((row) => {
        const createdAtDate = row?.created_at?.split('T')[0];
        return createdAtDate >= start && createdAtDate <= end;
    });
};

const createGVMDownloadRow = (row, useISODate = false) => {
    const dateField = useISODate
        ? 'contract_date_from_cet_iso'
        : 'contract_date_from_cet';
    const toDateField = useISODate
        ? 'contract_date_to_cet_iso'
        : 'contract_date_to_cet';

    return [
        row?.data?.seller || 'NA',
        row?.data?.buyer || 'NA',
        moment(row?.created_at).format('YYYY-MM-DD HH:mm:ss') || 'NA',
        moment(row?.created_at).format('YYYY-MM-DD') +
            '-' +
            row?.uuid?.split('-')[4] || 'NA',
        row?.uuid || 'NA',
        row?.data?.deliverypoint || 'NA',
        row?.data?.volume || 'NA',
        moment(row?.[dateField])?.format('YYYY-MM-DD HH:mm:ss') || 'NA',
        moment(row?.[toDateField])?.format('YYYY-MM-DD HH:mm:ss') || 'NA',
        row?.order_price_str || 'NA',
        row?.data?.currency || 'NA',
        row?.username || 'NA',
        row?.data?.exitpoint?.name?.split('-')[1] || 'NA',
        row?.data?.exitpoint?.name?.split('|')[0] || 'NA',
        row?.data?.exitpoint?.name?.split('|')[1]?.split('-')[0] || 'NA'
    ].join(';');
};

const createDefaultDownloadRow = (row) => {
    return [
        row?.data?.sym || 'NA',
        row?.data?.buyer || 'NA',
        row?.data?.seller || 'NA',
        row?.data?.deliverypoint || 'NA',
        row?.data?.volume || 'NA',
        row?.data?.deliverypoint === 'PEG' ? 'MWh/day' : 'MW/h' || 'NA',
        row?.order_price_str || 'NA',
        row?.data?.currency || 'NA',
        row?.data?.type || 'NA',
        row?.order_status || 'Completed',
        row?.username || 'NA',
        row?.created_at || 'NA',
        row?.contract_date_from_cet || 'NA',
        row?.contract_date_to_cet || 'NA'
    ].join(';');
};

const downloadCSV = (data, filename) => {
    const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

// Component helper functions
const createStyledTypography = (
    text,
    color = 'black',
    fontSize = 'inherit'
) => <Typography sx={{ fontSize, color }}>{text}</Typography>;

const getUnitsByDeliveryPoint = (deliverypoint) =>
    deliverypoint === 'PEG' ? 'MWh/day' : 'MWh';

const getTypeColor = (type) =>
    type === 'sell' ? 'rgba(235, 105, 105, 1)' : 'rgba(39, 169, 255, 1)';

const formatNumberWithCommas = (value) =>
    value?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '';

const getRowHighlightColor = (row) =>
    row?.fxtraded ? 'rgba(76, 175, 80, 0.16)' : 'inherit';

// Column definition factory
const createColumns = (
    handleTradeFXClick,
    isAddingNewFXOrderLog,
    canTradeFX
) => [
    {
        header: 'Contract',
        id: 'contract',
        accessorFn: ({ contract }) => contract || '',
        Cell: ({ row }) =>
            createStyledTypography(
                row?.original?.data?.sym,
                '#3B9D9D',
                'inherit'
            ),
        size: 50
    },
    {
        header: 'Buyer',
        id: 'buyer',
        accessorFn: ({ buyer }) => buyer || '',
        Cell: ({ row }) => createStyledTypography(row?.original?.data?.buyer),
        size: 30
    },
    {
        header: 'Seller',
        id: 'seller',
        accessorFn: ({ seller }) => seller || '',
        Cell: ({ row }) => createStyledTypography(row?.original?.data?.seller),
        size: 30
    },
    {
        header: 'Delivery-Point',
        id: 'deliverypoint',
        accessorFn: ({ deliverypoint }) => deliverypoint || '',
        Cell: ({ row }) =>
            createStyledTypography(row?.original?.data?.deliverypoint),
        size: 30
    },
    {
        header: 'Volume',
        id: 'volume_str',
        accessorFn: ({ volume_str }) => formatNumberWithCommas(volume_str),
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
        size: 50
    },
    {
        header: 'Unit',
        id: 'unit',
        Cell: ({ row }) =>
            createStyledTypography(
                getUnitsByDeliveryPoint(row?.original?.data?.deliverypoint),
                '#27A9FF',
                'default'
            ),
        size: 30
    },
    {
        header: 'Price',
        id: 'order_price_str',
        accessorFn: ({ order_price_str }) =>
            formatNumberWithCommas(order_price_str),
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
        size: 50
    },
    {
        header: 'Currency',
        id: 'currency',
        accessorFn: ({ currency }) => currency || '',
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
        size: 50
    },
    {
        header: 'Type',
        id: 'type',
        Cell: ({ row }) => {
            const type = row?.original?.data?.type;
            return createStyledTypography(
                type === 'sell' ? 'Sell' : 'Buy',
                getTypeColor(type)
            );
        },
        size: 50
    },
    {
        header: 'Status',
        id: 'order_status',
        accessorFn: ({ order_status }) => order_status || 'Completed',
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
        size: 50
    },
    {
        header: 'Account',
        id: 'username',
        accessorFn: ({ username }) => username || '',
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
        size: 50
    },
    {
        header: 'Created-At',
        id: 'created_at',
        accessorFn: ({ created_at }) => created_at || '',
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
        size: 50
    },
    {
        header: 'Delivery Start',
        id: 'contract_date_from_cet',
        accessorFn: ({ contract_date_from_cet }) => contract_date_from_cet || '',
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
        size: 50
    },
    {
        header: 'Delivery End',
        id: 'contract_date_to_cet',
        accessorFn: ({ contract_date_to_cet }) => contract_date_to_cet || '',
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
        size: 50
    },

    // {
    //     header: 'Actions',
    //     id: 'actions',
    //     Cell: ({ row }) => (
    //         <Box sx={{ display: 'flex', gap: '0.5rem' }}>
    //             {/* Action buttons can be added here */}
    //             <Button
    //                 variant="contained"
    //                 size="small"
    //                 disabled={!canTradeFX}
    //                 onClick={(e) => {
    //                     e.stopPropagation();
    //                     handleTradeFXClick(row.original);
    //                 }}
    //                 sx={{
    //                     backgroundColor: row?.original?.fxtraded
    //                         ? 'rgba(76, 175, 80, 0.7)'
    //                         : '#0b9cfd',
    //                     '&:hover': {
    //                         backgroundColor: row?.original?.fxtraded
    //                             ? 'rgba(76, 175, 80, 0.7)'
    //                             : '#0b9cfd'
    //                     }
    //                 }}
    //             >
    //                 {/* {row?.original?.fxtraded ? 'FX Traded' : 'Trade FX'} */}
    //                 Trade FX
    //             </Button>
    //         </Box>
    //     ),
    //     size: 50
    // }
];

const OrderLog = ({ openModal, handleCloseModal }) => {
    const dispatch = useDispatch();

    const submitToFXOrderLog = async (order) => {
        const resp = await dispatch(apiAddFXOrderLog(order));
        if (resp?.meta?.requestStatus === 'fulfilled') {
            showToast('success', 'Order added to FX Log successfully');
            dispatch(apiGetOrder());
        } else if (resp?.meta?.requestStatus === 'rejected') {
            // showToast('error', 'Failed to add order to FX Log');
            showToast(
                'error',
                resp?.payload || 'Failed to add order to FX Log'
            );
        }
    };
    const { order, isGetOrderLoading, isAddingNewFXOrderLog } = useSelector(
        (state) => state.order
    );
    const { user } = useSelector((state) => state.user);

    useEffect(() => {
        dispatch(apiGetOrder());
    }, [dispatch]);

    const [anchorEl, setAnchorEl] = useState(null);
    const [filterType, setFilterType] = useState(FILTER_TYPES.ALL);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [openDetailModal, setOpenDetailModal] = useState(false);
    const [selectedFXOrder, setSelectedFXOrder] = useState(null);
    const [openFXTradeModal, setOpenFXTradeModal] = useState(false);

    const handleRowClick = (row) => {
        setSelectedOrder(row);
        setOpenDetailModal(true);
    };

    const handleCloseDetailModal = () => {
        setOpenDetailModal(false);
        setSelectedOrder(null);
    };

    const handleTradeFXClick = useCallback((row) => {
        setSelectedFXOrder(row);
        setOpenFXTradeModal(true);
    }, []);

    const handleCloseFXTradeModal = useCallback(() => {
        if (isAddingNewFXOrderLog) return;

        setOpenFXTradeModal(false);
        setSelectedFXOrder(null);
    }, [isAddingNewFXOrderLog]);

    const handleConfirmFXTrade = useCallback(async () => {
        if (!selectedFXOrder || isAddingNewFXOrderLog) return;

        await submitToFXOrderLog(selectedFXOrder);
        handleCloseFXTradeModal();
    }, [
        selectedFXOrder,
        isAddingNewFXOrderLog,
        handleCloseFXTradeModal,
        submitToFXOrderLog
    ]);

    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleFilterChange = useCallback((type) => {
        setFilterType(type);
        handleClose();
    }, []);

    const filteredOrders = useMemo(() => {
        if (!order?.length) return [];

        switch (filterType) {
            case FILTER_TYPES.ONE_DAY:
                return filterOrdersByDateRange(order, 1);
            case FILTER_TYPES.ONE_WEEK:
                return filterOrdersByDateRange(order, 7);
            case FILTER_TYPES.ONE_MONTH:
                return filterOrdersByDateRange(order, 30);
            case FILTER_TYPES.ALL:
            default:
                return order;
        }
    }, [order, filterType]);

    const getDownloadData = useCallback(() => {
        return (
            filteredOrders?.map((row) =>
                user?.Company === 'GVM'
                    ? createGVMDownloadRow(row, true)
                    : createDefaultDownloadRow(row)
            ) || []
        );
    }, [filteredOrders, user?.Company]);

    const handleGVMCompanyFormatDownload = useCallback(() => {
        const header = GVM_COMPANY_DOWNLOAD_FORMAT.join(';');
        const data = getDownloadData();
        const csv = `${header}\n${data.join('\n')}`;
        downloadCSV(csv, 'order_log.csv');
    }, [getDownloadData]);

    const handleDefaultFormatDownload = useCallback(() => {
        const header = DEFAULT_DOWNLOAD_FORMAT.join(';');
        const data = getDownloadData();
        const csv = `${header}\n${data.join('\n')}`;
        downloadCSV(csv, 'order_log.csv');
    }, [getDownloadData]);

    const canTradeFX =
        user?.CompanyShort === 'TC' || user?.CompanyShort === 'SET';

    const columns = useMemo(
        () =>
            createColumns(
                handleTradeFXClick,
                isAddingNewFXOrderLog,
                canTradeFX
            ),
        [handleTradeFXClick, isAddingNewFXOrderLog, canTradeFX]
    );

    const renderToolbar = ({ table }) => (
        <Box sx={{ display: 'flex' }}>
            <>
                <Tooltip title="Filter">
                    <IconButton
                        aria-controls={open ? 'basic-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={handleClick}
                        sx={{
                            background: 'none',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            p: '0.5rem',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                    >
                        <FilterAltOutlinedIcon sx={{ color: 'gray' }} />
                    </IconButton>
                </Tooltip>
                <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{ 'aria-labelledby': 'basic-button' }}
                >
                    <MenuItem
                        onClick={() => handleFilterChange(FILTER_TYPES.ONE_DAY)}
                    >
                        One day
                    </MenuItem>
                    <MenuItem
                        onClick={() =>
                            handleFilterChange(FILTER_TYPES.ONE_WEEK)
                        }
                    >
                        One week
                    </MenuItem>
                    <MenuItem
                        onClick={() =>
                            handleFilterChange(FILTER_TYPES.ONE_MONTH)
                        }
                    >
                        One month
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleFilterChange(FILTER_TYPES.ALL)}
                    >
                        All
                    </MenuItem>
                </Menu>
            </>
            <Tooltip
                title={
                    user?.Company === 'GVM'
                        ? 'Download GVM CSV'
                        : 'Download CSV'
                }
            >
                <IconButton
                    onClick={
                        user?.Company === 'GVM'
                            ? handleGVMCompanyFormatDownload
                            : handleDefaultFormatDownload
                    }
                >
                    <DownloadIcon sx={{ color: 'gray' }} />
                </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
                <IconButton onClick={() => dispatch(apiGetOrder())}>
                    <RefreshIcon sx={{ color: 'gray' }} />
                </IconButton>
            </Tooltip>
            <MRT_ToggleGlobalFilterButton table={table} />
        </Box>
    );

    const renderTable = (headerText) => (
        <ReactTable
            renderToolbarInternalActions={renderToolbar}
            headerText={headerText}
            columns={columns}
            data={filteredOrders}
            isLoading={isGetOrderLoading}
            showCellBorders={true}
            handleRowClick={handleRowClick}
            muiTableBodyRowProps={({ row }) => ({
                sx: {
                    backgroundColor: getRowHighlightColor(row?.original),
                    cursor: 'pointer',
                    '&:hover td': {
                        backgroundColor: row?.original?.fxtraded
                            ? 'rgba(76, 175, 80, 0.24)'
                            : 'inherit'
                    }
                }
            })}
        />
    );

    const tabs = [
        {
            name: 'Order Log',
            content: (
                <>
                    <Modal open={openModal} onClose={handleCloseModal}>
                        <AddEntry
                            handleClose={handleCloseModal}
                            open={openModal}
                        />
                    </Modal>
                    <Modal
                        open={openDetailModal}
                        onClose={handleCloseDetailModal}
                    >
                        <OrderLogDetail
                            handleClose={handleCloseDetailModal}
                            order={selectedOrder}
                        />
                    </Modal>
                    <Modal
                        open={openFXTradeModal}
                        onClose={handleCloseFXTradeModal}
                    >
                        <Box
                            className="modal-container"
                            sx={{ minWidth: '420px' }}
                        >
                            <Box className="modal-header">
                                <Typography
                                    sx={{
                                        color: '#245372',
                                        fontSize: '1.25rem',
                                        fontWeight: 700
                                    }}
                                >
                                    Confirm FX Trade
                                </Typography>
                            </Box>
                            <Box className="modal-body">
                                <Typography sx={{ color: '#245372' }}>
                                    Do you want to trade FX for this order?
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '13px',
                                        color: '#43a047',
                                        fontWeight: 500,
                                        mt: 1
                                    }}
                                >
                                    {selectedFXOrder?.fxtraded &&
                                        'This order has been already traded for FX.'}
                                </Typography>
                            </Box>
                            <Box
                                className="modal-footer"
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '12px'
                                }}
                            >
                                <Button
                                    className="modal-button-secondary"
                                    onClick={handleCloseFXTradeModal}
                                    disabled={isAddingNewFXOrderLog}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    className="modal-button-primary"
                                    onClick={handleConfirmFXTrade}
                                    disabled={isAddingNewFXOrderLog}
                                    sx={{ minWidth: '110px' }}
                                >
                                    {isAddingNewFXOrderLog ? (
                                        <CircularProgress
                                            size={20}
                                            sx={{ color: 'white' }}
                                        />
                                    ) : (
                                        'Confirm'
                                    )}
                                </Button>
                            </Box>
                        </Box>
                    </Modal>
                    {renderTable('Order Logs')}
                </>
            )
        }
        // ,
        // {
        //     name: 'FX Order Log',
        //     content: <FXOrderLog />
        // }
    ];

    return <TabComponent tabs={tabs} />;
};

export default OrderLog;
